import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
	BudgetCaptureResponse,
	BudgetEntryDTO,
	BudgetEntryListResponse,
	BudgetEntryResponse,
	ConfirmCaptureResponse,
	CreateBudgetEntryBody,
	SetBudgetEntryStatusBody,
	UpdateBudgetEntryBody,
} from "@zplanem/responses"
import { ApiError, apiFetch, uploadToGcs } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { budgetBasePath, budgetEntryPath } from "./budget-api-paths"

/**
 * Property budget list + summary (ZPL-224). Owner-only — for a non-owner the
 * API 404s, which the page treats as "no budget for you" (tab is hidden too).
 * Out-of-view changes (chat-accepted expenses, extraction fills, other
 * devices) arrive via the owner-only `budget-entry.changed` ping (ZPL-301) —
 * no client-side polling.
 */
export function useBudgetEntries(
	propertyId: string,
	opts?: { projectId?: string },
) {
	const projectId = opts?.projectId
	return useQuery({
		queryKey: projectId
			? [...queryKeys.propertyBudget(propertyId), { projectId }]
			: queryKeys.propertyBudget(propertyId),
		queryFn: () =>
			apiFetch<BudgetEntryListResponse>(
				projectId
					? `${budgetBasePath(propertyId)}?projectId=${projectId}`
					: budgetBasePath(propertyId),
			),
		enabled: !!propertyId,
		// A 404 is the API's definitive "not yours" (owner-only) — retrying only
		// delays the page's dashboard redirect.
		retry: (failureCount, err) =>
			!(err instanceof ApiError && err.status === 404) && failureCount < 3,
	})
}

function useInvalidateBudget(propertyId: string) {
	const qc = useQueryClient()
	return () =>
		qc.invalidateQueries({ queryKey: queryKeys.propertyBudget(propertyId) })
}

/** Manual entry ("z palca") — created directly as `confirmed`. */
export function useCreateBudgetEntry(propertyId: string) {
	const invalidate = useInvalidateBudget(propertyId)
	return useMutation({
		mutationFn: (body: CreateBudgetEntryBody) =>
			apiFetch<BudgetEntryResponse>(budgetBasePath(propertyId), {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: invalidate,
	})
}

export function useUpdateBudgetEntry(propertyId: string) {
	const invalidate = useInvalidateBudget(propertyId)
	return useMutation({
		mutationFn: (input: { entryId: string; body: UpdateBudgetEntryBody }) =>
			apiFetch<BudgetEntryResponse>(
				budgetEntryPath(propertyId, input.entryId),
				{
					method: "PATCH",
					body: JSON.stringify(input.body),
				},
			),
		onSuccess: invalidate,
	})
}

/** Accept (`confirmed`, requires an amount) or reject an AI draft. */
export function useSetBudgetEntryStatus(propertyId: string) {
	const invalidate = useInvalidateBudget(propertyId)
	return useMutation({
		mutationFn: (input: {
			entryId: string
			status: SetBudgetEntryStatusBody["status"]
		}) =>
			apiFetch<BudgetEntryResponse>(
				`${budgetEntryPath(propertyId, input.entryId)}/status`,
				{
					method: "POST",
					body: JSON.stringify({ status: input.status }),
				},
			),
		onSuccess: invalidate,
	})
}

export function useDeleteBudgetEntry(propertyId: string) {
	const invalidate = useInvalidateBudget(propertyId)
	return useMutation({
		mutationFn: (entryId: string) =>
			apiFetch<{ deleted: true }>(budgetEntryPath(propertyId, entryId), {
				method: "DELETE",
			}),
		onSuccess: invalidate,
	})
}

interface CaptureResult {
	entry: BudgetEntryDTO
	possibleDuplicate: boolean
}

/**
 * Flow-B invoice capture, mirroring the documents 3-step signed-URL pattern:
 * 1. POST /capture — creates the proof doc + the ai_draft entry, returns a
 *    signed GCS upload target;
 * 2. PUT the bytes to GCS;
 * 3. POST /:entryId/confirm-capture — validates + enqueues extraction.
 * The extraction itself is async — the list poller picks up the filled draft.
 */
export function useCaptureInvoice(propertyId: string) {
	const invalidate = useInvalidateBudget(propertyId)
	return useMutation({
		mutationFn: async (input: {
			file: File
			projectId?: string | null
		}): Promise<CaptureResult> => {
			const captured = await apiFetch<BudgetCaptureResponse>(
				`${budgetBasePath(propertyId)}/capture`,
				{
					method: "POST",
					body: JSON.stringify({
						contentType: input.file.type,
						filename: input.file.name,
						declaredSize: input.file.size,
						...(input.projectId ? { projectId: input.projectId } : {}),
					}),
				},
			)
			await uploadToGcs(captured.upload, input.file)
			const confirmed = await apiFetch<ConfirmCaptureResponse>(
				`${budgetEntryPath(propertyId, captured.entry.id)}/confirm-capture`,
				{ method: "POST" },
			)
			return {
				entry: confirmed.entry,
				possibleDuplicate: confirmed.possibleDuplicate,
			}
		},
		onSuccess: invalidate,
	})
}
